import axios from 'axios';
import { generatePath } from 'react-router-dom';

import {
  calculateDpiaRecommendation,
  doesDecisionDeviateFromRecommendation,
  pickCurrentDpiaIdFromHistory,
} from 'config/api/dpiaRequests/dpiaRequests.helpers';
import type {
  DpiaRequest,
  DpiaRequestContext,
  DpiaRequestDecision,
  DpiaRequestHistoryItem,
  DpiaRequestTriggerSource,
  DpiaScreeningCriterion,
  DpiaScreeningCriterionCode,
  SaveDpiaRequestDraftInput,
} from 'config/api/dpiaRequests/dpiaRequests.types';
import PATHS from 'router/PATHS';

export type DpiaNeedAssessmentFormValues = {
  triggerSource: DpiaRequestTriggerSource;
  triggerComment: string;
  selectedCriteria: DpiaScreeningCriterionCode[];
  criteriaAssessmentComment: string;
  alwaysDpiaRuleTriggered: boolean;
  alwaysDpiaRuleComment: string;
  decision: DpiaRequestDecision | '';
  decisionComment: string;
};

export type DpiaNeedAssessmentValidationMessages = {
  required: string;
  triggerComment: string;
  alwaysRuleComment: string;
  decisionComment: string;
  decisionRequired: string;
};

const FIELD_ERROR_MAP: Record<string, keyof DpiaNeedAssessmentFormValues> = {
  trigger_source: 'triggerSource',
  trigger_comment: 'triggerComment',
  criteria_codes: 'selectedCriteria',
  selected_criteria: 'selectedCriteria',
  criteria_comment: 'criteriaAssessmentComment',
  criteria_assessment_comment: 'criteriaAssessmentComment',
  always_dpia_rule_triggered: 'alwaysDpiaRuleTriggered',
  always_dpia_rule_comment: 'alwaysDpiaRuleComment',
  decision_comment: 'decisionComment',
  decision: 'decision',
};

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord => typeof value === 'object' && value !== null && !Array.isArray(value);

export const DPIA_REQUEST_NOT_EDITABLE_CODE = 'dpia_request_not_editable';

export const isDpiaAlreadyInProgressError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const { data } = error.response ?? {};
  if (!isRecord(data)) {
    return false;
  }

  return JSON.stringify(data).toLowerCase().includes('already in progress');
};

export const getExistingDpiaIdFromError = (error: unknown): number | null => {
  if (!axios.isAxiosError(error)) {
    return null;
  }

  const { data } = error.response ?? {};
  if (!isRecord(data)) {
    return null;
  }

  const raw = data.dpia_id ?? data.created_dpia_id ?? data.existing_dpia_id;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
};

export { pickCurrentDpiaIdFromHistory };

export const mapRequestToFormValues = (request: DpiaRequest): DpiaNeedAssessmentFormValues => ({
  triggerSource: request.triggerSource,
  triggerComment: request.triggerComment,
  selectedCriteria: request.selectedCriteria,
  criteriaAssessmentComment: request.criteriaAssessmentComment,
  alwaysDpiaRuleTriggered: request.alwaysDpiaRuleTriggered,
  alwaysDpiaRuleComment: request.alwaysDpiaRuleComment,
  decision: request.decision ?? '',
  decisionComment: request.decisionComment,
});

export const mapFormValuesToDraftInput = (values: DpiaNeedAssessmentFormValues): SaveDpiaRequestDraftInput => ({
  triggerSource: values.triggerSource,
  triggerComment: values.triggerComment,
  selectedCriteria: values.selectedCriteria,
  criteriaAssessmentComment: values.criteriaAssessmentComment,
  alwaysDpiaRuleTriggered: values.alwaysDpiaRuleTriggered,
  alwaysDpiaRuleComment: values.alwaysDpiaRuleComment,
  decision: values.decision || null,
});

export const getLiveRecommendation = (values: DpiaNeedAssessmentFormValues) =>
  calculateDpiaRecommendation(values.selectedCriteria, values.alwaysDpiaRuleTriggered);

export const getLiveDecisionDeviation = (values: DpiaNeedAssessmentFormValues) => {
  if (!values.decision) {
    return false;
  }

  return doesDecisionDeviateFromRecommendation(values.decision, getLiveRecommendation(values));
};

export const validateDraftForm = (
  values: DpiaNeedAssessmentFormValues,
  messages: DpiaNeedAssessmentValidationMessages,
): Partial<Record<keyof DpiaNeedAssessmentFormValues | 'form', string>> => {
  const errors: Partial<Record<keyof DpiaNeedAssessmentFormValues | 'form', string>> = {};

  if (!values.triggerComment.trim()) {
    errors.triggerComment = messages.triggerComment;
  }

  if (values.alwaysDpiaRuleTriggered && !values.alwaysDpiaRuleComment.trim()) {
    errors.alwaysDpiaRuleComment = messages.alwaysRuleComment;
  }

  return errors;
};

export const validateDecisionForm = (
  values: DpiaNeedAssessmentFormValues,
  messages: DpiaNeedAssessmentValidationMessages,
): Partial<Record<keyof DpiaNeedAssessmentFormValues | 'form', string>> => {
  const errors = validateDraftForm(values, messages);

  if (!values.decision) {
    errors.decision = messages.decisionRequired;
  }

  if (!values.decisionComment.trim()) {
    errors.decisionComment = messages.decisionComment;
  }

  return errors;
};

export const hasValidationErrors = (errors: Partial<Record<string, string>>) => Object.keys(errors).length > 0;

export const isDpiaRequestEditable = (status: DpiaRequest['status']) => status === 'draft';

export const getAxiosStatus = (error: unknown): number | undefined => (axios.isAxiosError(error) ? error.response?.status : undefined);

export const getAxiosErrorCode = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const { data } = error.response ?? {};
  if (!isRecord(data)) {
    return undefined;
  }

  if (typeof data.code === 'string') {
    return data.code;
  }

  if (typeof data.error_code === 'string') {
    return data.error_code;
  }

  if (typeof data.detail === 'string') {
    return data.detail;
  }

  return undefined;
};

const firstErrorMessage = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim()) {
    return value;
  }

  if (Array.isArray(value)) {
    const firstString = value.find((item): item is string => typeof item === 'string' && item.trim().length > 0);
    return firstString;
  }

  return undefined;
};

export const mapDpiaFieldErrorsFromApi = (error: unknown): Partial<Record<keyof DpiaNeedAssessmentFormValues, string>> => {
  if (!axios.isAxiosError(error)) {
    return {};
  }

  const { data } = error.response ?? {};
  if (!isRecord(data)) {
    return {};
  }

  const errors: Partial<Record<keyof DpiaNeedAssessmentFormValues, string>> = {};
  Object.entries(data).forEach(([key, value]) => {
    const formKey = FIELD_ERROR_MAP[key];
    const message = firstErrorMessage(value);
    if (formKey && message) {
      errors[formKey] = message;
    }
  });

  return errors;
};

export const isMissingOperationalOwnerError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  const { data } = error.response ?? {};
  if (!isRecord(data)) {
    return false;
  }

  const serialized = JSON.stringify(data).toLowerCase();
  return serialized.includes('operational owner') || serialized.includes('operational_owner');
};

export const isDpiaRequestNotEditableError = (error: unknown): boolean => {
  const status = getAxiosStatus(error);
  const code = getAxiosErrorCode(error);
  return status === 409 && (code === DPIA_REQUEST_NOT_EDITABLE_CODE || (typeof code === 'string' && code.includes('not_editable')));
};

export const getCriterionLabel = (criterion: DpiaScreeningCriterion, language: string): string => {
  const isSwedish = language.toLowerCase().startsWith('sv');
  const localized = isSwedish ? criterion.labelSv : criterion.labelEn;
  return localized || criterion.labelEn || criterion.code;
};

export const formatHistoryDate = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toISOString().slice(0, 10);
};

export const formatHistoryItem = (
  item: DpiaRequestHistoryItem,
  t: (message: string | string[]) => string,
  messages: {
    need_assessment: string[];
    decision: string[];
    status: string[];
    statuses: Record<string, string[]>;
  },
): { title: string; subtitle: string } => {
  const date = formatHistoryDate(item.assessedAt || item.approvedAt || item.createdAt);
  const statusLabel = messages.statuses[item.status] ? t(messages.statuses[item.status]) : item.status.replace(/_/g, ' ');

  if (item.kind === 'dpia') {
    return {
      title: item.title || t(messages.need_assessment),
      subtitle: `${t(messages.status)}: ${statusLabel}${date ? ` · ${date}` : ''}`,
    };
  }

  return {
    title: date ? `${t(messages.need_assessment)} ${date}` : t(messages.need_assessment),
    subtitle: `${t(messages.decision)}: ${statusLabel}`,
  };
};

export const formatCountryNames = (countryCodes: string[], countryNameByCode: Map<string, string>): string =>
  countryCodes.map(code => countryNameByCode.get(code.toUpperCase()) ?? code).join(', ');

export const getActivePurposeSummary = (context: DpiaRequestContext): string => {
  if (context.activePurposes.length > 0) {
    return context.activePurposes.map(purpose => purpose.purposeText).join(', ');
  }

  return String(context.activePurposesCount);
};

export const buildDpiaNeedAssessmentLocation = ({
  apaId,
  requestId,
  returnPath,
}: {
  apaId: number | string;
  requestId?: number;
  returnPath?: string;
}) => ({
  pathname: generatePath(PATHS.PROCESSING_ACTIVITIES_DPIA_ASSESSMENT, { apaId: String(apaId) }),
  ...(typeof requestId === 'number' ? { search: `?requestId=${requestId}` } : {}),
  state: {
    ...(returnPath ? { returnPath } : {}),
    ...(typeof requestId === 'number' ? { requestId } : {}),
  },
});
